// pages/SelfCoding/ManageQuestions.tsx
import { t } from "@lingui/macro";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AlertColor } from "@mui/material/Alert";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Alerts from "@/client/components/alerts";
import { useUser } from "@/client/services/user";

const ManageCodingQuestions = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const studentId = user?.id;

  const [coding, setCoding] = useState<any>(null);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [availableCount, setAvailableCount] = useState(0);
  const [filters, setFilters] = useState({
    search: "",
    difficulty: "",
    language: "",
    context: "",
    tags: "",
    limit: "",
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    fetchCoding();
  }, [id]);

  useEffect(() => {
    fetchAvailableQuestions();
  }, [filters]);

  const fetchCoding = async () => {
    try {
      const res = await axios.get(
        `/api/integration/external-service/self-coding/${id}`,
        { params: { studentId } }
      );
      setCoding(res.data.result);
    } catch (error) {
      console.error("Error fetching self-coding assessment:", error);
      setAlert({
        open: true,
        message: t`Failed to fetch Coding assessment details`,
        severity: "error",
      });
    }
  };

  const fetchAvailableQuestions = async () => {
    try {
      const params: any = {
        difficulty: filters.difficulty,
        context: filters.context,
        language: filters.language,
        tags: filters.tags,
        page: 1,
      };

      if (filters.limit && parseInt(filters.limit) > 0) {
        params.limit = parseInt(filters.limit);
      } else {
        params.limit = 10000;
      }

      const res = await axios.get(
        `/api/integration/external-service/self-coding/questions/available`,
        { params }
      );

      setAvailableQuestions(res.data.result.questions || []);
      setAvailableCount(res.data.result.count || 0);

      if (
        filters.limit &&
        parseInt(filters.limit) > 0 &&
        parseInt(filters.limit) > (res.data.result.count || 0)
      ) {
        setAlert({
          open: true,
          message: t`Only ${res.data.result.count} questions are available for the selected filters.`,
          severity: "warning",
        });
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      setAlert({
        open: true,
        message: t`Failed to fetch coding questions`,
        severity: "error",
      });
    }
  };

  const handleFilterChange = (e: any) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLimitChange = (e: any) => {
    const value = e.target.value;
    if (value === "" || (parseInt(value) > 0 && !isNaN(parseInt(value)))) {
      setFilters((prev) => ({
        ...prev,
        limit: value,
      }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      search: "",
      difficulty: "",
      language: "",
      context: "",
      tags: "",
      limit: "",
    });
  };

  const handleAddQuestions = async () => {
    if (selectedQuestions.length === 0) {
      setAlert({
        open: true,
        message: t`Please select at least one question`,
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `/api/integration/external-service/self-coding/${id}/questions`,
        { questionIds: selectedQuestions },
        { params: { studentId } }
      );
      setAlert({
        open: true,
        message: t`Questions added successfully`,
        severity: "success",
      });
      setSelectedQuestions([]);
      fetchCoding();
    } catch (error: any) {
      setAlert({
        open: true,
        message:
          error.response?.data?.error || t`Failed to add questions`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveQuestion = async (questionId: string) => {
    try {
      await axios.delete(
        `/api/integration/external-service/self-coding/${id}/question/${questionId}`,
        { params: { studentId } }
      );
      setAlert({
        open: true,
        message: t`Question removed successfully`,
        severity: "success",
      });
      fetchCoding();
    } catch (error: any) {
      setAlert({
        open: true,
        message:
          error.response?.data?.error || t`Failed to remove question`,
        severity: "error",
      });
    }
  };

  const handleToggleQuestion = (questionId: string) => {
    setSelectedQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, message: "", severity: "info" });
  };

  const handleContinue = () => {
    navigate(`/self-coding/create/${id}/1`);
  };

  return (
    <Box sx={{ py: 1 }}>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      <Container>
        {/* Back Button */}
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/self-coding/create/${id}/1`)}
            sx={{ textTransform: "none" }}
          >
            {t`Back`}
          </Button>
        </Box>

        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t`Filter and select questions to add to your Coding assessment`}
          </Typography>
        </Box>

        {/* Filters Card */}
        <Grid mb={3}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t`Programming Language`}</InputLabel>
                <Select
                  name="language"
                  value={filters.language}
                  onChange={handleFilterChange}
                  label={t`Programming Language`}
                >
                  <MenuItem value="">{t`All Languages`}</MenuItem>
                  <MenuItem value="java">{t`Java`}</MenuItem>
                  <MenuItem value="python">{t`Python`}</MenuItem>
                  <MenuItem value="javascript">{t`JavaScript`}</MenuItem>
                  <MenuItem value="typescript">{t`TypeScript`}</MenuItem>
                  <MenuItem value="cpp">{t`C++`}</MenuItem>
                  <MenuItem value="csharp">{t`C#`}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>{t`Difficulty Level`}</InputLabel>
                <Select
                  name="difficulty"
                  value={filters.difficulty}
                  onChange={handleFilterChange}
                  label={t`Difficulty Level`}
                >
                  <MenuItem value="">{t`All Levels`}</MenuItem>
                  <MenuItem value="beginner">{t`Beginner`}</MenuItem>
                  <MenuItem value="intermediate">{t`Intermediate`}</MenuItem>
                  <MenuItem value="advanced">{t`Advanced`}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>{t`Context`}</InputLabel>
                <Select
                  name="context"
                  value={filters.context}
                  onChange={handleFilterChange}
                  label={t`Context`}
                >
                  <MenuItem value="">{t`All Contexts`}</MenuItem>
                  <MenuItem value="loops">{t`Loops`}</MenuItem>
                  <MenuItem value="arrays">{t`Arrays`}</MenuItem>
                  <MenuItem value="functions">{t`Functions`}</MenuItem>
                  <MenuItem value="oop">{t`OOP`}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                size="small"
                label={t`No of Questions`}
                name="limit"
                type="number"
                value={filters.limit}
                onChange={handleLimitChange}
                placeholder={t`e.g., 10`}
                InputProps={{ inputProps: { min: 1 } }}
                sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ccc !important",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#999 !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#555 !important",
                        },
                      "& .MuiOutlinedInput-root.Mui-focused": {
                        boxShadow: "none !important",
                        outline: "none !important",
                      },
                      "& input:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                      },
                    }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={1}>
              <Tooltip title={t`Reset Filters`}>
                <Button
                  variant="contained"
                  onClick={handleResetFilters}
                  sx={{ textTransform: "none", height: "35px", mb: "2px" }}
                >
                  {t`Reset`}
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Available Questions */}
          <Grid item xs={12} lg={7}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent
                sx={{
                  px: 2,
                  py: 2,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 18 }}>
                      {t`Available Questions`}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${availableQuestions.length} ${t`questions`}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const allIds = availableQuestions.map((q: any) => q._id);
                      setSelectedQuestions(
                        selectedQuestions.length === availableQuestions.length
                          ? []
                          : allIds
                      );
                    }}
                    disabled={availableQuestions.length === 0}
                    sx={{ textTransform: "none" }}
                  >
                    {selectedQuestions.length === availableQuestions.length
                      ? t`Deselect All`
                      : t`Select All`}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    startIcon={<AddIcon />}
                    onClick={handleAddQuestions}
                    disabled={selectedQuestions.length === 0 || loading}
                    sx={{ textTransform: "none" }}
                  >
                    {t`Add Selected`} ({selectedQuestions.length})
                  </Button>
                </Box>

                {/* Questions List */}
                <Box sx={{ flex: 1, minHeight: "400px", maxHeight: "400px", overflow: "auto", pr: 1 }}>
                  {availableQuestions.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        textAlign: "center",
                        px: 2,
                      }}
                    >
                      <Box>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                          {filters.language
                            ? t`Select filters to view questions`
                            : t`No questions found`}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Stack spacing={1}>
                      {availableQuestions.map((question: any) => (
                        <Card
                          key={question._id}
                          variant="outlined"
                          sx={{
                            cursor: "pointer",
                            border: selectedQuestions.includes(question._id)
                              ? "1px solid #07182aff"
                              : "1px solid #e0e0e0",
                            "&:hover": {
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            },
                          }}
                          onClick={() => handleToggleQuestion(question._id)}
                        >
                          <CardContent sx={{ px: 0.5, pt: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 0.5,
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={selectedQuestions.includes(question._id)}
                                onChange={() =>
                                  handleToggleQuestion(question._id)
                                }
                                sx={{ mt: -1 }}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{ fontSize: "14px" }}>
                                  {question.question?.text || question.title || ""}
                                </Typography>
                                {question.language && (
                                  <Chip
                                    label={question.language}
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Added Questions */}
          <Grid item xs={12} lg={5}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent
                sx={{
                  px: 2,
                  py: 2,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 18 }}>
                      {t`Added Questions`}
                    </Typography>
                  </Box>
                  <Chip
                    label={coding?.questions?.length || 0}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                {/* Questions List */}
                <Box sx={{ flex: 1, minHeight: "445px", maxHeight: "445px", overflow: "auto", pr: 1 }}>
                  {coding?.questions?.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        textAlign: "center",
                        px: 2,
                      }}
                    >
                      <Box>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                          {t`No questions added yet`}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Stack spacing={1}>
                      {coding?.questions?.map(
                        (question: any, index: number) => (
                          <Card
                            key={question._id}
                            variant="outlined"
                            sx={{
                              transition: "all 0.2s ease",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              },
                            }}
                          >
                            <CardContent sx={{ px: 2, pt: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  gap: 2,
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body1" sx={{ fontSize: "14px" }}>
                                    {index + 1}. {question.question?.text || question.title || ""}
                                  </Typography>
                                </Box>
                                <Tooltip title={t`Remove Question`}>
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() =>
                                      handleRemoveQuestion(question._id)
                                    }
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </CardContent>
                          </Card>
                        )
                      )}
                    </Stack>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Footer Actions */}
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={handleContinue}
            sx={{ textTransform: "none", minWidth: 80 }}
          >
            {t`Finish`}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ManageCodingQuestions;